import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, LogIn, ShoppingBag, UserPlus } from "lucide-react";
import { createQuoteRequest } from "../services/quoteService";
import { friendlyError } from "../api/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import EmptyState from "../components/ui/EmptyState";
import { formatMoney } from "../utils/money";

export default function Quote() {
  const { items, clear } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { showPrices } = useSettings();
  const navigate = useNavigate();

  // "choose" = the Guest/Login/Create Account chooser (shown only to a
  // signed-out visitor); "form" = the actual request form. A logged-in
  // customer skips the chooser entirely and lands straight on "form" —
  // set once here from the auth state at the moment this page is
  // reached, which is exactly what's needed since returning from a
  // successful Login/Register navigates back to this page fresh.
  const [mode, setMode] = useState(isAuthenticated ? "form" : "choose");

  const [form, setForm] = useState({
    customerName: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    shippingAddress: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { qtn_code, status, item_count, created_at }

  if (items.length === 0 && !result) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-neutral-900 mb-6">Request a Quote</h1>
        <EmptyState title="Your cart is empty." note="Add products before requesting a quote." />
        <div className="text-center">
          <Link to="/shop" className="text-sm text-neutral-600 underline">
            Go to Shop
          </Link>
        </div>
      </div>
    );
  }

  // ── Success screen ──────────────────────────────────────────────────
  if (result) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="rounded-2xl border border-green-200 bg-green-50 p-6 sm:p-8 text-center">
          <CheckCircle2 className="mx-auto text-green-600" size={40} />
          <h1 className="mt-3 text-lg font-semibold text-green-800">Request Submitted</h1>
          <div className="mt-4 text-left inline-block text-sm text-neutral-700 space-y-1">
            <p>
              <span className="text-neutral-500">Quote Number:</span> <strong>{result.qtn_code}</strong>
            </p>
            <p>
              <span className="text-neutral-500">Name:</span> {form.customerName}
            </p>
            <p>
              <span className="text-neutral-500">Items:</span> {result.item_count}
            </p>
            <p>
              <span className="text-neutral-500">Status:</span> {result.status}
            </p>
          </div>
          <p className="mt-4 text-xs text-neutral-500">
            Keep your quote number. You can check its status later with this number and the phone or email you gave.
          </p>
          <Link
            to="/quote/status"
            state={{ qtnCode: result.qtn_code, phone: form.phone, email: form.email }}
            className="mt-3 inline-block text-sm text-neutral-700 underline"
          >
            Check quote status
          </Link>
        </div>

        {!isAuthenticated && (
          <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6 text-center">
            <p className="text-sm text-neutral-700">
              Would you like to create an account to manage your profile and make future orders easier?
            </p>
            <div className="mt-4 flex flex-col gap-2.5">
              <button
                onClick={() => navigate("/register")}
                className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800"
              >
                <UserPlus size={16} />
                Create Account
              </button>
              <Link
                to="/shop"
                className="w-full flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-50"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        )}

        {isAuthenticated && (
          <div className="text-center mt-6">
            <Link to="/shop" className="text-sm text-neutral-600 underline">
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    );
  }

  // ── Guest / Login / Create Account chooser ──────────────────────────
  // Shown only to a signed-out visitor, before any form fields — a
  // customer who is already logged in never sees this (mode starts at
  // "form" for them, above).
  if (mode === "choose") {
    return (
      <div className="max-w-md mx-auto px-4 py-12 sm:py-16">
        <h1 className="text-xl font-semibold text-neutral-900 text-center">How would you like to continue?</h1>
        <p className="text-sm text-neutral-500 text-center mt-2">
          You can request a quote without an account, or sign in to have your details filled in automatically.
        </p>

        <div className="mt-8 flex flex-col gap-3">
          <button
            onClick={() => setMode("form")}
            className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium rounded-xl px-4 py-3.5 hover:bg-neutral-800"
          >
            <ShoppingBag size={17} />
            Continue as Guest
          </button>
          <button
            onClick={() => navigate("/login", { state: { from: { pathname: "/quote" } } })}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-xl px-4 py-3.5 hover:bg-neutral-50"
          >
            <LogIn size={17} />
            Login
          </button>
          <button
            onClick={() => navigate("/register", { state: { from: { pathname: "/quote" } } })}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-xl px-4 py-3.5 hover:bg-neutral-50"
          >
            <UserPlus size={17} />
            Create Account
          </button>
        </div>

        <div className="text-center mt-8">
          <Link to="/cart" className="text-sm text-neutral-500 underline">
            Back to Cart
          </Link>
        </div>
      </div>
    );
  }

  // ── Guest / customer form (the existing flow, reused as-is) ─────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) {
      setError("Full name is required.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Phone number is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await createQuoteRequest({
        customerName: form.customerName,
        phone: form.phone,
        email: form.email,
        shippingAddress: form.shippingAddress,
        note: form.note,
        items: items.map((i) => ({
          product_id: i.product_id,
          unit_code: i.unit_code || null,
          qty_ordered: i.qty,
        })),
      });
      setResult(res);
      clear();
    } catch (err) {
      setError(friendlyError(err, "Unable to submit your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-neutral-900 mb-2">Request a Quote</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Review your items and submit a quote request. This is not an order or a payment — our team will
        follow up with you.
      </p>

      <div className="rounded-xl border border-neutral-200 bg-white p-4 mb-6">
        <h2 className="text-sm font-medium text-neutral-900 mb-3">Requested Items</h2>
        <ul className="text-sm text-neutral-700 space-y-1">
          {items.map((i) => (
            <li key={i.id} className="flex justify-between gap-3">
              <span>
                {i.name}
                {i.unit_code && <span className="text-neutral-400"> · {i.unit_code}</span>}
              </span>
              <span className="text-neutral-500 text-right">
                {showPrices && i.price != null
                  ? `${i.qty} × ${formatMoney(i.price, i.currency)} = ${formatMoney(i.qty * i.price, i.currency)}`
                  : `× ${i.qty}`}
              </span>
            </li>
          ))}
        </ul>
        {!showPrices && (
          <p className="text-xs text-neutral-400 mt-3">Price will be provided by the business.</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="q-name" className="block text-sm font-medium text-neutral-700 mb-1">
            Full Name *
          </label>
          <input
            id="q-name"
            required
            autoComplete="name"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="q-phone" className="block text-sm font-medium text-neutral-700 mb-1">
              Phone *
            </label>
            <input
              id="q-phone"
              required
              type="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
          <div>
            <label htmlFor="q-email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email <span className="text-neutral-400 font-normal">(optional)</span>
            </label>
            <input
              id="q-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
        </div>
        <div>
          <label htmlFor="q-address" className="block text-sm font-medium text-neutral-700 mb-1">
            Delivery Address <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <input
            id="q-address"
            autoComplete="street-address"
            value={form.shippingAddress}
            onChange={(e) => setForm({ ...form, shippingAddress: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div>
          <label htmlFor="q-note" className="block text-sm font-medium text-neutral-700 mb-1">
            Note <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="q-note"
            rows={3}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>

        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3.5 hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Submitting..." : "Submit Request"}
        </button>

        {!isAuthenticated && (
          <button
            type="button"
            onClick={() => setMode("choose")}
            className="w-full text-center text-sm text-neutral-500 underline"
          >
            Back
          </button>
        )}
      </form>
    </div>
  );
}
